import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Renders the AI narrative as SAFE lightweight markdown: paragraphs, bullet/numbered
 * lists, **bold**, *italic*, `code`, and inline record references of the form
 * [[kind:id|Name]] (rendered as chips that navigate to the record). Everything is
 * built as React elements — raw HTML is never parsed or injected.
 */
export function InsightsMarkdown({ text }: { text: string }): JSX.Element {
  const blocks = text.split(/\n{2,}/).filter((b) => b.trim().length > 0);
  return (
    <div className="space-y-2 text-sm leading-relaxed text-slate-800">
      {blocks.map((block, i) => (
        <Block key={i} block={block} />
      ))}
    </div>
  );
}

function Block({ block }: { block: string }): JSX.Element {
  const lines = block.split('\n');
  const isBullet = lines.every((l) => /^\s*[-*]\s+/.test(l));
  const isNumbered = lines.every((l) => /^\s*\d+[.)]\s+/.test(l));
  if (isBullet || isNumbered) {
    const items = lines.map((l) => l.replace(/^\s*(?:[-*]|\d+[.)])\s+/, ''));
    const List = isBullet ? 'ul' : 'ol';
    return (
      <List className={`ml-5 space-y-1 ${isBullet ? 'list-disc' : 'list-decimal'}`}>
        {items.map((item, i) => (
          <li key={i}>
            <Inline text={item} />
          </li>
        ))}
      </List>
    );
  }
  // Headings the model was told not to use still degrade gracefully to bold text.
  const heading = /^#{1,4}\s+(.*)$/.exec(block.trim());
  if (heading) {
    return (
      <p className="font-semibold text-slate-900">
        <Inline text={heading[1]!} />
      </p>
    );
  }
  return (
    <p className="whitespace-pre-wrap">
      <Inline text={block} />
    </p>
  );
}

/** Routes for inline record chips (member intentionally absent — no detail route). */
const CHIP_ROUTES: Record<string, (id: string) => string> = {
  customer: (id) => `/customers?returnCustomerId=${encodeURIComponent(id)}`,
  project: (id) => `/projects/${id}`,
  agreement: (id) => `/agreements/${id}`,
  ticket: (id) => `/tickets/${id}`,
  invoice: (id) => `/invoices/${id}`,
  opportunity: (id) => `/sales/opportunities/${id}`,
};

/** Inline tokens, matched in order: record chip, bold, code, italic. */
const INLINE_TOKEN =
  /\[\[([a-z_]+):([^|\]]+)\|([^\]]+)\]\]|\*\*([^*]+)\*\*|`([^`]+)`|\*([^*\s][^*]*)\*/g;

function Inline({ text }: { text: string }): JSX.Element {
  const parts: JSX.Element[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE_TOKEN)) {
    if (match.index! > last) {
      parts.push(<Fragment key={key++}>{text.slice(last, match.index)}</Fragment>);
    }
    const [, chipKind, chipId, chipName, bold, code, italic] = match;
    if (chipKind !== undefined) {
      parts.push(<RecordChip key={key++} kind={chipKind} id={chipId!} name={chipName!} />);
    } else if (bold !== undefined) {
      parts.push(
        <strong key={key++} className="font-semibold text-slate-900">
          <Inline text={bold} />
        </strong>,
      );
    } else if (code !== undefined) {
      parts.push(
        <code key={key++} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em]">
          {code}
        </code>,
      );
    } else if (italic !== undefined) {
      parts.push(
        <em key={key++}>
          <Inline text={italic} />
        </em>,
      );
    }
    last = match.index! + match[0].length;
  }
  if (last < text.length) parts.push(<Fragment key={key++}>{text.slice(last)}</Fragment>);
  return <>{parts}</>;
}

function RecordChip({ kind, id, name }: { kind: string; id: string; name: string }): JSX.Element {
  const navigate = useNavigate();
  const route = CHIP_ROUTES[kind];
  // Unknown kinds (or a model mistake) degrade to the plain name.
  if (!route || !id.trim()) return <>{name}</>;
  const path = route(id.trim());
  return (
    <button
      type="button"
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey) window.open(path, '_blank', 'noopener');
        else navigate(path);
      }}
      title={`Open ${kind}`}
      className="mx-0.5 inline-flex items-baseline rounded-md border border-brand-200 bg-brand-50 px-1.5 py-0 text-[0.9em] font-medium text-brand-700 hover:bg-brand-100"
    >
      {name}
    </button>
  );
}
